import { z } from "zod";

/**
 * VERSİYON ODAKLI AUDIT GÖRÜNÜMÜ (CLOS-143 `[102]`).
 *
 * ### ⚠ Audit içerik DEĞİLDİR
 *
 * Kart iki yetkiyi açıkça ayırıyor: *"audit geçmişini görüntüleme permission'ı sözleşme
 * içeriğini indirme permission'ı vermez; içerik erişimi de organizasyon audit kayıtlarını
 * otomatik açmaz."* Bu yüzden görünüm hiçbir yerde sözleşme metni, dosya adresi ya da
 * çıkarılmış metin taşımıyor — yalnız "kim, ne zaman, neyi yaptı".
 *
 * ### ⚠ Görünen olay kümesi KAPALI
 *
 * Kart: *"yalnız kullanıcının görmeye yetkili olduğu OLAY TİPLERİNİ ve GÜVENLİ
 * aktör/metadata alanlarını döner."* Ham audit satırını olduğu gibi vermek, ileride
 * eklenecek her yeni olayı ve her yeni `after` alanını bu ekrana sessizce sızdırırdı.
 * Liste burada; listede olmayan olay görünmez.
 */

export const CONTRACT_VERSION_AUDIT_EVENTS = [
  "contract.version_created",
  "contract.version_role_changed",
  "contract.version_downloaded",
  "contract.version_download_blocked",
] as const;
export type ContractVersionAuditEvent = (typeof CONTRACT_VERSION_AUDIT_EVENTS)[number];

export const CONTRACT_VERSION_AUDIT_EVENT_NAMES: Record<ContractVersionAuditEvent, string> = {
  "contract.version_created": "Versiyon oluşturuldu",
  "contract.version_role_changed": "Versiyon rolü değişti",
  "contract.version_downloaded": "Belge indirildi",
  "contract.version_download_blocked": "Belge indirme engellendi",
};

/**
 * Engellenme sebebi.
 *
 * ⚠ Sebep AUDIT'te duruyor, YANITTA değil: indirmeye çalışan kullanıcıya "karantinada" ya
 * da "kaynak kaldırıldı" demek, kaydın durumu hakkında bilgi vermek olurdu (`[079]`
 * varlık sızdırmama kararı). Denetim yapan kişi ise sebebi görmek zorunda.
 */
export const CONTRACT_DOWNLOAD_BLOCK_REASONS = [
  "source_missing",
  "source_purged",
  "not_deliverable",
  "metadata_mismatch",
  "access_denied",
] as const;
export type ContractDownloadBlockReason = (typeof CONTRACT_DOWNLOAD_BLOCK_REASONS)[number];

export const CONTRACT_DOWNLOAD_BLOCK_REASON_NAMES: Record<ContractDownloadBlockReason, string> = {
  source_missing: "Versiyonun kaynak dosyası yok",
  source_purged: "Kaynak dosya kaldırılmış",
  not_deliverable: "Dosya teslim edilebilir durumda değil",
  metadata_mismatch: "Dosya bilgileri kayıtla uyuşmuyor",
  access_denied: "Erişim reddedildi",
};

/**
 * Tek bir audit satırının GÜVENLİ temsili.
 *
 * ⚠ `actorName` var, `actorId` YOK. Kart: *"aktör hesabı kapalı olsa bile tarihsel kimlik
 * güvenli temsille korunur; GEREKSİZ KİŞİSEL VERİ açılmaz."* Görünen ad zaten kaydın
 * yazıldığı anda dondurulmuş bir bilgi; ham kullanıcı kimliği ise başka yüzeylerde
 * eşleştirme yapmaya yarayan bir tanımlayıcı ve bu ekranın işine yaramıyor.
 */
export type ContractVersionAuditEntry = {
  id: string;
  event: ContractVersionAuditEvent;
  eventName: string;
  actorName: string;
  /** Aktörün olay ANINDAKİ rolü; bugünkü rolü değil. */
  actorRole: string | null;
  createdAt: string;
  /** Olaya özgü güvenli alanlar — sözleşme metni ve dosya adresi asla girmez. */
  detail: Record<string, string | number | boolean | null>;
};

export const contractVersionAuditQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  /** İmleç: son görülen kaydın zamanı. Sayfa numarası DEĞİL — audit araya kayıt alır. */
  /**
   * Sayfalama imleci — OPAK BİLEŞİK (`<zaman>|<kimlik>`), düz tarih DEĞİL.
   *
   * ⚠ Önceden ISO tarihiydi ve **satır düşürüyordu**: Postgres `timestamptz` MİKROsaniye
   * tutuyor, `Date.toISOString()` MİLİsaniyeye kırpıyor; azalan sırada kırpılmış imleç
   * gerçek değerden küçük kalıyor ve aradaki olaylar `<` koşuluna hiç girmiyor. Ölçüldü:
   * aynı milisaniyedeki 6 olayda sayfalama 2'sini gösterdi.
   *
   * Kimlik ikinci anahtar olarak duruyor ki eşit zamanlı olaylar da atlanmasın. İstemci
   * imleci ÇÖZMEZ, olduğu gibi geri gönderir.
   */
  before: z
    .string()
    .regex(/^[^|]{1,64}\|[0-9a-f-]{36}$/, "Geçersiz imleç")
    .optional(),
  event: z.enum(CONTRACT_VERSION_AUDIT_EVENTS).optional(),
});
export type ContractVersionAuditQuery = z.infer<typeof contractVersionAuditQuerySchema>;

export type ContractVersionAuditPage = {
  items: ContractVersionAuditEntry[];
  nextCursor: string | null;
};
